import { lazy, useEffect, useMemo, useState, useRef, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  matchPath,
  useLocation,
} from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { initSentry } from "@/config/sentry";
import { SentryErrorFallback } from "@/components/ErrorDisplay/SentryErrorFallback";
import {
  AuthProvider,
  resolveLandingPath,
  LOGIN_PATH,
  useAuth,
} from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ThemeProvider as BlueprintThemeProvider } from "styled-components";
import { light as blueprintTheme } from "@resideo/blueprint-theme";
import { ToastContextProvider, GlobalStyle } from "@resideo/blueprint-react";
import { ToastContextProvider as OnePortalToastContextProvider } from "@resideo/blueprint-oneportal";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Login } from "@/pages/Login";
// import { ContextSelection } from "@/pages/ContextSelection";
import { Auth0Callback } from "@/pages/Auth0Callback";
import BusinessIntelligence from "@/pages/BusinessIntelligence";
import { DeviceHealth } from "@/pages/DeviceHealth";
import ManageDevices from "@/pages/ManageDevices/ManageDevices";
import { Employees, InviteEmployee, EmployeeDetail } from "@/pages/Employees";
import { Profile } from "@/pages/Profile";
import AddDevice from "@/pages/AddDevice";
import EditControls from "@/pages/EditControls";
// Lazy-loaded so the transitive pilet CSS (Create/Review Weekly Schedule
// module.css) is injected AFTER Blueprint's styled-components runtime sheet.
// An eager import would put those styles first in the cascade and let
// Blueprint's `.dObvlT` win at equal specificity, breaking `.editButton` etc.
// on the pilet's own /thermostat/schedules page too.
const DevicesSchedules = lazy(
  () => import("@/pages/DevicesSchedules/DevicesSchedules"),
);
import { Unauthorized } from "@/pages/Unauthorized";
import BulkEditSettings from "@/pages/BulkEditSettings/BulkEditSettings";
import ReviewApplyPage from "@/pages/BulkEditSettings/ReviewApplyPage";
import ReplaceSchedulePage from "@/pages/BulkEditSettings/ReplaceSchedulePage";
import NotificationBulkSchedulePage from "@/pages/BulkEditSettings/NotificationBulkSchedulePage";
import ManageLocations from "@/pages/ManageLocations/ManageLocations";
import { SignalRProviderWrapper } from "@/contexts/SignalRProviderWrapper";
import { piletLoader } from "@/services/piletLoader";
import { setAuth0TokenGetter } from "@/services/apiService";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import { useTranslation } from "react-i18next";
import { featureFlagService } from "@/services/featureFlagService";
import "./App.css";
import "./styles/theme.css";

export const matchesPublicAuthRoute = (
  pathname: string,
  publicRoutes: Array<{ path: string }>,
): boolean =>
  publicRoutes.some((route) => matchPath({ path: route.path, end: true }, pathname));

// Context-aware root redirect component
const RootRedirect = () => {
  const { selectedContext, isInitializing, activeRole, user } = useAuth();
  const hasRedirectedRef = useRef(false);

  console.log("🎯 RootRedirect render:", {
    selectedContext,
    isInitializing,
    activeRole,
    hasRedirected: hasRedirectedRef.current,
    userEmail: user?.email,
  });

  // Wait for initialization and active role to be set
  if (isInitializing || !activeRole) {
    console.log("⏳ RootRedirect: Waiting for initialization...", {
      isInitializing,
      activeRole,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        Loading...
      </div>
    );
  }

  // Prevent double redirect
  if (hasRedirectedRef.current) {
    console.log("⏭️ RootRedirect: Already redirected, rendering null");
    return null;
  }

  hasRedirectedRef.current = true;

  const targetPath = resolveLandingPath(selectedContext ?? "", user?.id);
  console.log(
    `🔀 RootRedirect: Redirecting to ${targetPath} (context=${selectedContext}, role=${activeRole})`,
  );
  return <Navigate to={targetPath} replace />;
};

// Initialize Sentry before React renders
initSentry();

function App() {
  const [piletsLoaded, setPiletsLoaded] = useState(false);
  const [piletRoutes, setPiletRoutes] = useState<any[]>([]);
  const [publicRoutes, setPublicRoutes] = useState<any[]>([]);
  const { t } = useTranslation();

  // Check if using mock auth or real Auth0
  const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === "true";

  useEffect(() => {
    // Load pilets on app startup
    const loadResources = async () => {
      try {
        // Initialize feature flag service first (non-blocking)
        await featureFlagService.initialize();
        console.log("✅ Feature flags initialized");

        // Load pilets from npm packages (runtime integration)
        await piletLoader.loadAllPilets();
        setPiletRoutes(piletLoader.getRoutes());
        setPublicRoutes(piletLoader.getPublicRoutes());
        setPiletsLoaded(true);
      } catch (error) {
        console.error("Failed to load resources:", error);
        setPiletsLoaded(true);
      }
    };

    loadResources();
  }, []);

  const auth0Config = {
    domain: import.meta.env.VITE_AUTH0_DOMAIN || "resideo.auth0.com",
    clientId:
      import.meta.env.VITE_AUTH0_CLIENT_ID ||
      "x27JIDVbRAVgDCnItaJjJBIwhk8hWtPC",
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience:
        import.meta.env.VITE_AUTH0_AUDIENCE || "https://api.oneportal.com",
      scope: "openid profile email offline_access",
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true, // Fallback to non-refresh tokens if offline_access not granted
    // Default is 60s; a failing prompt=none iframe otherwise leaves the page blank that long.
    authorizeTimeoutInSeconds: 10,
    cacheLocation: "localstorage" as const,
  };

  // Memoized so App re-renders keep the same component type; inline definitions remount the whole provider tree.
  const Auth0TokenInitializer = useMemo(() => () => {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

    // Proactively keep the token fresh — refreshes ~60 s before expiry
    useTokenRefresh();

    useEffect(() => {
      // Register an options-aware getter so apiService can request a
      // force-refresh (cacheMode: 'off') when handling 401 responses.
      if (!useMockAuth) {
        setAuth0TokenGetter(async (opts) => {
          try {
            // No cast to `any` here — passing `opts` directly keeps TS on the
            // non-verbose overload (Promise<string | undefined>) instead of
            // ambiguously resolving to the `detailedResponse: true` overload.
            const token = await getAccessTokenSilently(opts);
            if (!token) {
              throw new Error("Auth0 did not return an access token");
            }
            return token;
          } catch (error) {
            console.error("Failed to get Auth0 access token:", error);
            throw error;
          }
        });
      }
    }, [getAccessTokenSilently]);

    // Store Auth0 token in localStorage for Pro-Portal pilet.
    // Also remove it on logout so the Guard View window receives a storage
    // event and can permanently block all new HTTP API calls (while keeping
    // existing WebRTC connections alive).
    useEffect(() => {
      let cancelled = false;
      const storeToken = async () => {
        // Do not attempt to fetch/store a fresh token while Auth0 is still
        // initializing. This prevents race conditions where a pending
        // token fetch overwrites an existing token during the SDK
        // initialization window.
        if (isAuthenticated && !isLoading && !useMockAuth) {
          try {
            const token = await getAccessTokenSilently();
            if (cancelled) return;
            if (token) {
              localStorage.setItem("authToken", token);
              console.log("✅ Auth0 token stored in localStorage for Pro-Portal");
            }
          } catch (error) {
            console.error("Failed to store Auth0 token:", error);
          }
        } else if (!isLoading && !isAuthenticated && !useMockAuth) {
          // Only remove after Auth0 has finished initializing (isLoading === false).
          // Removing during initialization would fire the storage event in Guard
          // View windows and permanently lock them on a normal page refresh.
          localStorage.removeItem("authToken");
        }
      };
      storeToken();
      return () => {
        cancelled = true;
      };
    }, [isAuthenticated, isLoading, getAccessTokenSilently]);

    return null;
  }, []);

  const Auth0AuthenticationFlow = useMemo(() => ({
    routesReady,
    publicRoutes,
  }: {
    routesReady: boolean;
    publicRoutes: Array<{ path: string }>;
  }) => {
    const {
      isAuthenticated,
      isLoading: auth0Loading,
      error: auth0Error,
      loginWithRedirect,
    } = useAuth0();
    const { user: appUser, isInitializing } = useAuth();
    const { pathname, search } = useLocation();
    const redirectStarted = useRef(false);
    const [retryKey, setRetryKey] = useState(0);
    const [redirectError, setRedirectError] = useState(false);

    const isLoginRoute = pathname === LOGIN_PATH;
    const isPublicRoute = matchesPublicAuthRoute(pathname, publicRoutes);
    const callbackParams = new URLSearchParams(search);
    const isAuth0Callback = Boolean(auth0Error) ||
      (callbackParams.has("code") && callbackParams.has("state")) ||
      callbackParams.has("error");

    useEffect(() => {
      if (
        !routesReady ||
        auth0Loading ||
        isInitializing ||
        isAuthenticated ||
        isLoginRoute ||
        isPublicRoute ||
        isAuth0Callback ||
        redirectStarted.current
      ) {
        return;
      }

      redirectStarted.current = true;
      sessionStorage.removeItem("oneportal_logout_complete");
      setRedirectError(false);
      void loginWithRedirect().catch((error: unknown) => {
        redirectStarted.current = false;
        setRedirectError(true);
        console.error("Auth0 login error:", error);
      });
    }, [
      routesReady,
      auth0Loading,
      isInitializing,
      isAuthenticated,
      isLoginRoute,
      isPublicRoute,
      isAuth0Callback,
      loginWithRedirect,
      retryKey,
    ]);

    if (isAuth0Callback) {
      return <Auth0Callback />;
    }

    if (isInitializing || isLoginRoute || isPublicRoute || !routesReady) {
      return null;
    }

    if (isAuthenticated && !appUser) {
      return <Auth0Callback />;
    }

    if (redirectError) {
      return (
        <div role="alert" className="auth-redirect-error">
          <p>Unable to start sign-in. Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => {
              setRedirectError(false);
              setRetryKey((current) => current + 1);
            }}
          >
            Retry sign-in
          </button>
        </div>
      );
    }

    return null;
  }, []);

  const AppContent = () => (
    <BlueprintThemeProvider theme={blueprintTheme}>
      <GlobalStyle />
      <ToastContextProvider>
        <OnePortalToastContextProvider>
          <BrowserRouter>
            <SignalRProviderWrapper>
              <ThemeProvider>
                <AuthProvider>
                  {/* Initialize Auth0 token getter if not in mock mode */}
                  {!useMockAuth && <Auth0TokenInitializer />}
                  {!useMockAuth && (
                    <Auth0AuthenticationFlow
                      routesReady={piletsLoaded}
                      publicRoutes={publicRoutes}
                    />
                  )}

                  <Routes>
                    <Route
                      path={LOGIN_PATH}
                      element={
                        <Login autoStart={!useMockAuth} mockMode={useMockAuth} />
                      }
                    />
                    {/* Context selection removed — the landing context is
                        resolved from account types in Auth0Callback.
                    <Route
                      path="/context-selection"
                      element={<ContextSelection />}
                    />
                    */}

                    {/* Public pilet routes (no authentication required) */}
                    {piletsLoaded &&
                      publicRoutes.map((route, index) => (
                        <Route
                          key={`public-${index}`}
                          path={route.path}
                          element={route.element}
                        />
                      ))}

                    <Route
                      path="/"
                      element={
                        <ProtectedRoute redirectUnauthenticated={useMockAuth}>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={
                          <ProtectedRoute>
                            <RootRedirect />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="onboarding" element={null} />
                      <Route
                        path="business-intelligence"
                        element={<BusinessIntelligence />}
                      />
                      <Route path="device-health" element={<DeviceHealth />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="locations" element={<ManageLocations />} />
                      <Route path="devices" element={<ManageDevices />} />
                      <Route path="devices/add" element={<AddDevice />} />
                      <Route
                        path="devices/edit-controls"
                        element={<EditControls />}
                      />
                      {/* Bulk "Set Schedules" — reuses the pilet's
                          AddPeriodScheduleTypeScreen → CreateWeeklySchedule
                          flow for the thermostats selected on ManageDevices. */}
                      <Route
                        path="devices/schedules"
                        element={
                          <Suspense fallback={null}>
                            <DevicesSchedules />
                          </Suspense>
                        }
                      />

                      {/* Bulk Edit Settings — edit shared settings (including the recording
                          schedule) across multiple cameras. Fully hosted in the shell. */}
                      <Route path="camera/bulk-edit" element={<BulkEditSettings />} />
                      <Route path="camera/bulk-edit/review" element={<ReviewApplyPage />} />
                      <Route path="camera/bulk-edit/replace-schedule" element={<ReplaceSchedulePage />} />
                      <Route
                        path="camera/bulk-edit/notification-schedule"
                        element={
                          <ProtectedRoute featureFlag="cameras.notifications">
                            <NotificationBulkSchedulePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Dynamic pilet routes loaded from npm packages */}
                      {piletsLoaded &&
                        piletRoutes.map((route, index) => (
                          <Route
                            key={index}
                            path={route.path}
                            element={route.element}
                          />
                        ))}

                      {/* Placeholder routes for future pilets */}

                      <Route
                        path="thermostat"
                        element={
                          <ProtectedRoute requiredPermission="module.thermostat.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thermostatModule')}</h1>
                              <p>{t('placeholders.thermostatModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="thermostat/schedules"
                        element={
                          <ProtectedRoute requiredPermission="module.thermostat.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thermostatSchedules')}</h1>
                              <p>{t('placeholders.thermostatSchedulesDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="panel"
                        element={
                          <ProtectedRoute requiredPermission="module.panel.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.panelModule')}</h1>
                              <p>{t('placeholders.panelModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="water-leak"
                        element={
                          <ProtectedRoute requiredPermission="module.water-leak.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.waterLeakModule')}</h1>
                              <p>{t('placeholders.waterLeakModuleDesc')}</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="third-party-devices"
                        element={
                          <ProtectedRoute requiredPermission="module.third-party-devices.view">
                            <div style={{ padding: "24px" }}>
                              <h1>{t('placeholders.thirdPartyModule')}</h1>
                              <p>
                                This will be provided by the third-party-devices-pilet
                              </p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees"
                        element={
                          <ProtectedRoute featureFlag="employees" requiredPermission="user.view" deniedRoles={["GUEST"]}>
                            <Employees />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees/invite"
                        element={
                          <ProtectedRoute featureFlag="employees" requiredPermission="user.invite" deniedRoles={["GUEST"]}>
                            <InviteEmployee />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="employees/:id"
                        element={
                          <ProtectedRoute featureFlag="employees" requiredPermission="user.view" deniedRoles={["GUEST"]}>
                            <EmployeeDetail />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="users"
                        element={
                          <ProtectedRoute requiredPermission="module.users.view">
                            <div style={{ padding: "24px" }}>
                              <h1>Users Module</h1>
                              <p>This will be provided by the users-pilet</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="roles"
                        element={
                          <ProtectedRoute requiredPermission="module.roles.view">
                            <div style={{ padding: "24px" }}>
                              <h1>Roles Module</h1>
                              <p>This will be provided by the roles-pilet</p>
                            </div>
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    <Route path="/unauthorized" element={<Unauthorized />} />
                  </Routes>
                </AuthProvider>
              </ThemeProvider>
            </SignalRProviderWrapper>
          </BrowserRouter>
        </OnePortalToastContextProvider>
      </ToastContextProvider>
    </BlueprintThemeProvider>
  );

  // Conditionally wrap with Auth0Provider based on environment
  return (
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <SentryErrorFallback
          {...errorData}
          error={errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))}
        />
      )}
      showDialog={false}
    >
      {useMockAuth ? (
        AppContent()
      ) : (
        <Auth0Provider {...auth0Config}>
          {AppContent()}
        </Auth0Provider>
      )}
    </Sentry.ErrorBoundary>
  );
}

export default App;